import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'astroturf/react'

import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';

import { FeedContext } from 'js/social/contexts';
import * as api from 'js/social/api';
import { useFeedConfig, useUsers, useNews } from 'js/social/Feed/hooks';
import { FeedPage } from 'js/social/Feed/hooks/useFeedConfig';
import { NewsType } from 'js/social/shared/types';
import useNewsInteractions from 'js/social/News/useNewsInteractions';
import {
  SocialEditor,
  SeeMore,
} from 'js/social/shared/SocialEditor/SocialEditor';
import { ImpressionPropsGenerator } from 'js/social/ImpressionHelper';
import { ImpressionEntity } from 'js/utils/impressions';
import { useCurrentUserAsAuthor } from 'js/social/shared/CreateSocialItem';

import { Comments } from '../News/Comments';
import { NewsInteractionBar } from '../News/NewsInteractionBar';
import { AuthorHeader } from '../News/AuthorHeader';
import SidebarAddComment from './SidebarAddComment';
import { mergeUserActions, mergeNewsActions } from './combineNewsActions';
import { CreateComment } from '../CreateSocialItem/CreateComment';
import { TruncateHeight } from 'js/social/shared/SocialEditor/TruncateHeight';
import _SidebarCommentsWrapper from "SidebarComments-SidebarCommentsWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Sidebar/SidebarComments.tsx?styleId=SidebarCommentsWrapper";
import _Separator from "SidebarComments-Separator.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Sidebar/SidebarComments.tsx?styleId=Separator";
import _PostContentWrapper from "SidebarComments-PostContentWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Sidebar/SidebarComments.tsx?styleId=PostContentWrapper";
import _PostSeeMore from "SidebarComments-PostSeeMore.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Sidebar/SidebarComments.tsx?styleId=PostSeeMore";
import _PostContentClassname from "SidebarComments-PostContentClassname.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Sidebar/SidebarComments.tsx?styleId=PostContentClassname";
import _AuthorsComment from "SidebarComments-AuthorsComment.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Sidebar/SidebarComments.tsx?styleId=AuthorsComment";
import _InteractionBarWrapper from "SidebarComments-InteractionBarWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Sidebar/SidebarComments.tsx?styleId=InteractionBarWrapper";


const SidebarCommentsWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "SidebarCommentsWrapper",
  styles: _SidebarCommentsWrapper
});

const Separator = /*#__PURE__*/styled("div", null, {
  displayName: "Separator",
  styles: _Separator
});

const PostContentWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "PostContentWrapper",
  styles: _PostContentWrapper
});

const PostSeeMore = /*#__PURE__*/styled(SeeMore, null, {
  displayName: "PostSeeMore",
  styles: _PostSeeMore
});

const PostContentClassname = _PostContentClassname.cls2;

const AuthorsComment = /*#__PURE__*/styled(Body, null, {
  displayName: "AuthorsComment",
  styles: _AuthorsComment
});

const InteractionBarWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "InteractionBarWrapper",
  styles: _InteractionBarWrapper
});

type Props = {
  newsType: NewsType;
  newsId: number | string;
  page: FeedPage;
  impressionPropsGen: ImpressionPropsGenerator;
};

function emptyNewsApi() {
  return async () => ({
    posts: [],
    users: [],
    meta: {
      next: null,
    },
  });
}

export const SidebarComments = ({
  newsType,
  newsId,
  page,
  impressionPropsGen,
}: Props) => {
  const [showAddCommentModal, setShowAddCommentModal] = useState(false);

  const getPage = useMemo(
    () =>
      newsType === 'paper_update'
        ? api.getSingleNewsPage(newsId, newsType, null)
        : emptyNewsApi(),
    [newsId, newsType],
  );

  const existingContext = useContext(FeedContext);

  const feedConfig = useFeedConfig(page);
  const innerUserActions = useUsers();
  const userActions = useMemo(
    () => mergeUserActions(existingContext.userActions, innerUserActions),
    [existingContext.userActions, innerUserActions],
  );

  const innerNewsActions = useNews({
    page: feedConfig.page,
    getPage,
    setMoreUsers: userActions.setMoreUsers,
    setFeedConfig: feedConfig.setFeedConfig,
  });
  const newsActions = useMemo(
    () => mergeNewsActions(existingContext.newsActions, innerNewsActions),
    [existingContext.newsActions, innerNewsActions],
  );

  useEffect(() => {
    newsActions.refreshFeed();
    // newsActions often gets regenerated but we really only want to refresh the
    // feed once at the beginning of the sidebar loading. Ideally newsActions
    // would only get refreshed when the actual newsType/newsId changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newsItem = newsActions.news.find((n) => n.newsId === newsId);
  const author = newsItem?.userId
    ? userActions.getUserData(newsItem.userId)
    : null;

  const currentUser = useCurrentUserAsAuthor();

  const { actions, permissions } = useNewsInteractions({
    newsId,
    newsType,
    paperType: null,
    userId: newsItem?.userId,
    tags: [],
    reason: newsItem?.reason,
    createdAt: newsItem?.createdAt,
    work: newsItem?.work,
    ...newsActions,
    ...userActions,
  });

  // This hijacks the impression props generated for `<Comments />` and adds a
  // prefix to the elements to distinguish these from the ones rendered in the
  // main newsfeed.
  const commentImpressionPropsGen: ImpressionPropsGenerator = useCallback(
    (entityType?: ImpressionEntity, entityId?: number | string) =>
      (element?: string) =>
      (clickTarget?: string) =>
        impressionPropsGen(
          entityType,
          entityId,
        )(element ? `sidebar-${element}` : undefined)(clickTarget),
    [impressionPropsGen],
  );

  if (!newsItem || !author || newsItem.newsType !== 'paper_update') {
    return null;
  }

  return (
    <FeedContext.Provider
      value={{
        feedConfig,
        userActions,
        newsActions,
        useReporting: false,
        recordViews: false,
        showInlineReader: false,
      }}
    >
      <SidebarCommentsWrapper>
        <AuthorHeader
          kind="user"
          user={author}
          size="md"
          showAvatar={true}
          showTimestamp={false}
          impressionPropsGen={impressionPropsGen}
        />
        {newsItem.content && (
          <PostContentWrapper>
            <AuthorsComment size="md" bold>
              Author’s comment on the paper
            </AuthorsComment>
            <TruncateHeight height={20 * 2} styledMoreElement={<PostSeeMore />}>
              <SocialEditor
                containerClassName={PostContentClassname}
                content={newsItem.content}
                tags={newsItem.tags}
                readOnly
              />
            </TruncateHeight>
          </PostContentWrapper>
        )}
        <InteractionBarWrapper>
          <NewsInteractionBar
            isLiked={newsItem.reactions.like.reacted}
            numLikes={newsItem.reactions.like.count}
            onLike={actions.likeNews}
            onUnlike={actions.unlikeNews}
            isBookmarked={!!permissions.isBookmarked}
            numBookmarks={newsItem.work?.bookmarkCount ?? 0}
            onBookmark={actions.bookmarkWork}
            onUnbookmark={actions.unbookmarkWork}
            numComments={newsItem.comments.count}
            onAddComment={() => setShowAddCommentModal(true)}
            impressionPropsGen={impressionPropsGen()()}
          />
        </InteractionBarWrapper>
        <Comments
          newsType={newsType}
          newsId={newsId}
          comments={newsItem.comments.data}
          getAuthor={(comment) => userActions.getUserData(comment.userId)}
          impressionPropsGen={commentImpressionPropsGen}
        />
        {newsItem.comments.count === 0 ? (
          <SidebarAddComment
            onStartComment={() => setShowAddCommentModal(true)}
            impressionPropsGen={impressionPropsGen}
          />
        ) : (
          <Button
            size="sm"
            onClick={() => setShowAddCommentModal(true)}
            icon="add"
          >
            Add your comment
          </Button>
        )}
        <Separator />
      </SidebarCommentsWrapper>
      {showAddCommentModal && (
        <CreateComment
          newsId={newsId}
          author={currentUser}
          replyTo={{
            kind: 'user',
            user: author,
            timestamp: newsItem.createdAt,
          }}
          replyToContent={
            newsItem.content
              ? {
                  content: newsItem.content,
                  tags: newsItem.tags,
                }
              : undefined
          }
          replyToWork={newsItem.work}
          onClose={() => setShowAddCommentModal(false)}
          onSubmit={actions.createComment}
          impressionPropsGen={impressionPropsGen}
        />
      )}
    </FeedContext.Provider>
  );
};
